<script setup lang="ts" generic="T extends FilesCardProps">
import type { FilesCardProps } from '../FilesCard/types'
import type { FileListProps, AttachmentsEmits } from './types'
import { ArrowLeftBold, ArrowRightBold, Plus, UploadFilled } from '@element-plus/icons-vue'
import { debounce } from 'radash'
import FilesCard from '../FilesCard/index.vue'

const props = withDefaults(defineProps<FileListProps>(), {
  items: () => [],
  overflow: 'scrollX',
  listStyle: () => ({}),
  uploadIconSize: '64px',
  dragTarget: undefined,
  hideUpload: false,
})

const emit = defineEmits<AttachmentsEmits>()

const containerRef = ref<HTMLDivElement | null>(null)
const wrapperRef = ref<HTMLDivElement | null>(null)
const pingStart = ref(false)
const pingEnd = ref(false)
const TOLERANCE = 1

function checkPing() {
  const el = containerRef.value
  if (!el) return
  if (props.overflow === 'scrollX') {
    pingStart.value = Math.abs(el.scrollLeft) >= TOLERANCE
    pingEnd.value = el.scrollWidth - el.clientWidth - Math.abs(el.scrollLeft) >= TOLERANCE
  } else if (props.overflow === 'scrollY') {
    pingStart.value = el.scrollTop !== 0
    pingEnd.value = el.scrollHeight - el.clientHeight !== el.scrollTop
  } else {
    pingStart.value = false
    pingEnd.value = false
  }
}

function onScrollOffset(offset: -1 | 1) {
  const el = containerRef.value
  if (!el) return
  el.scrollTo({
    left: props.overflow === 'scrollX' ? el.scrollLeft + offset * el.clientWidth : el.scrollLeft,
    top: props.overflow === 'scrollY' ? el.scrollTop + offset * el.clientHeight : el.scrollTop,
    behavior: 'smooth',
  })
}

function onScrollLeft() { onScrollOffset(-1) }
function onScrollRight() { onScrollOffset(1) }
const debouncedCheckPing = debounce({ delay: 100 }, checkPing)

// Upload/drag
const targetElement = ref<HTMLElement | null>(null)
const isTargetDrag = ref(false)
const debouncedStyle = debounce({ delay: 100 }, (isDrag: boolean) => {
  isTargetDrag.value = isDrag
  if (!targetElement.value) return
  const isBodyTarget = targetElement.value === document.body
  if (isDrag) {
    if (isBodyTarget && dropAreaRef.value) {
      dropAreaRef.value.style.position = 'fixed'
      dropAreaRef.value.style.width = '100vw'
      dropAreaRef.value.style.height = '100vh'
      dropAreaRef.value.style.left = '0'
      dropAreaRef.value.style.top = '0'
    } else if (!isBodyTarget) {
      targetElement.value.style.position = 'relative'
    }
  } else {
    targetElement.value.style.position = ''
  }
})

const dropAreaRef = ref<HTMLDivElement | null>(null)
const _isOverLimit = computed(() => props.limit ? props.items.length >= props.limit : false)

function handleUploadChange(file: File, props: FileListProps) { emit('uploadChange', file, props) }
function handleUploadSuccess(res: any, file: File, props: FileListProps) { emit('uploadSuccess', res, file, props) }
function handleUploadError(err: any, file: File, props: FileListProps) { emit('uploadError', err, file, props) }

function getTargetElement() {
  if (!props.dragTarget) return wrapperRef.value
  if (props.dragTarget instanceof HTMLElement) return props.dragTarget
  if (typeof props.dragTarget === 'string') return document.getElementById(props.dragTarget)
  if (isRef(props.dragTarget)) return (props.dragTarget as any).value as HTMLElement
  return wrapperRef.value
}

function targetDragEnter(e: DragEvent) { e.preventDefault(); debouncedStyle(true) }
function targetDragOver(e: DragEvent) { e.preventDefault() }
function targetDropLeave(e: DragEvent) {
  e.preventDefault()
  const rt = e.relatedTarget as Node
  if (targetElement.value?.contains(rt)) return
  debouncedStyle(false)
}
function targetDrop(e: DragEvent) {
  e.preventDefault()
  debouncedStyle(false)
  if (e.dataTransfer?.files?.length) emit('uploadDrop', [...e.dataTransfer.files], { ...props })
}

function handleDelete(item: FilesCardProps, index: number) {
  emit('deleteCard', item, index)
  nextTick(() => debouncedCheckPing())
}

onMounted(() => {
  nextTick(() => debouncedCheckPing())
  window.addEventListener('resize', debouncedCheckPing)
  if (wrapperRef.value) {
    targetElement.value = getTargetElement() || wrapperRef.value
    targetElement.value.addEventListener('dragenter', targetDragEnter, false)
    targetElement.value.addEventListener('dragleave', targetDropLeave, false)
    targetElement.value.addEventListener('drop', targetDrop, false)
    targetElement.value.addEventListener('dragover', targetDragOver, false)
  }
})

onUnmounted(() => {
  window.removeEventListener('resize', debouncedCheckPing)
  if (targetElement.value) {
    targetElement.value.removeEventListener('dragenter', targetDragEnter, false)
    targetElement.value.removeEventListener('dragleave', targetDropLeave, false)
    targetElement.value.removeEventListener('drop', targetDrop, false)
    targetElement.value.removeEventListener('dragover', targetDragOver, false)
  }
})

watch(() => [props.overflow, props.items.length], () => nextTick(() => debouncedCheckPing()), { immediate: true, deep: true })
watch(() => props.dragTarget, () => {
  nextTick(() => {
    if (wrapperRef.value) {
      targetElement.value = getTargetElement() || wrapperRef.value
      targetElement.value.addEventListener('dragenter', targetDragEnter, false)
      targetElement.value.addEventListener('dragleave', targetDropLeave, false)
      targetElement.value.addEventListener('drop', targetDrop, false)
      targetElement.value.addEventListener('dragover', targetDragOver, false)
    }
  })
}, { immediate: true, deep: true })

defineExpose({ onScrollLeft, onScrollRight, debouncedCheckPing })
</script>

<template>
  <div
    ref="wrapperRef"
    class="elx-attachments-wrapper"
    :class="{
      'elx-attachments-overflow-ping-start': overflow === 'scrollX' && pingStart,
      'elx-attachments-overflow-ping-end': overflow === 'scrollX' && pingEnd,
    }"
    :style="{ ...listStyle, '--elx-attachments-upload-icon-size': uploadIconSize }"
  >
    <!-- Empty state upload -->
    <div v-if="!items.length && !hideUpload">
      <slot name="empty-upload">
        <el-upload
          class="elx-attachments-upload-btn"
          v-bind="$attrs"
          :show-file-list="false"
          @change="handleUploadChange"
          @success="handleUploadSuccess"
          @error="handleUploadError"
        >
          <el-icon class="elx-attachments__uploader-icon"><Plus /></el-icon>
        </el-upload>
      </slot>
    </div>

    <!-- Scroll edge fade -->
    <div class="elx-attachments-background">
      <div v-if="overflow === 'scrollX' && pingStart" class="elx-attachments-background-start" />
      <div v-if="overflow === 'scrollX' && pingEnd" class="elx-attachments-background-end" />
    </div>

    <!-- File list container -->
    <div
      ref="containerRef"
      class="elx-attachments"
      :class="{ [`elx-attachments-overflow-${overflow}`]: overflow }"
      :style="{
        ...(overflow === 'scrollX' ? { whiteSpace: 'nowrap', overflowX: 'auto', overflowY: 'hidden' } : {}),
        ...(overflow === 'scrollY' ? { overflowX: 'hidden', overflowY: 'auto' } : {}),
        ...(overflow === 'wrap' ? { display: 'flex', flexWrap: 'wrap' } : {}),
      }"
      @scroll="debouncedCheckPing"
    >
      <div v-if="items.length" :class="{ 'elx-attachments-file-card-wrap': overflow === 'scrollX' }">
        <slot name="file-list" :items="items">
          <div v-for="(item, index) in items" :key="item.uid" class="elx-attachments-card">
            <Transition name="elx-attachments-card-motion">
              <FilesCard
                v-if="item.uid"
                v-bind="item"
                class="elx-attachments-card-item"
                @delete="handleDelete(item, index)"
              />
            </Transition>
          </div>
        </slot>

        <div v-if="items.length && !_isOverLimit && !hideUpload" class="elx-attachments-upload-placeholder">
          <slot name="no-empty-upload">
            <el-upload
              v-bind="$attrs"
              :show-file-list="false"
              :style="{ height: overflow === 'scrollY' ? '' : undefined }"
              class="elx-attachments-upload-btn"
              @change="handleUploadChange"
              @success="handleUploadSuccess"
              @error="handleUploadError"
            >
              <template #trigger>
                <el-icon class="elx-attachments__uploader-icon"><Plus /></el-icon>
              </template>
            </el-upload>
          </slot>
        </div>
      </div>
    </div>

    <!-- Scroll prev button -->
    <slot name="prev-button" :show="overflow === 'scrollX' && pingStart" :on-scroll-left="onScrollLeft">
      <el-button v-if="overflow === 'scrollX' && pingStart" size="small" class="elx-attachments-prev-btn" @click="onScrollLeft">
        <el-icon><ArrowLeftBold /></el-icon>
      </el-button>
    </slot>

    <!-- Scroll next button -->
    <slot name="next-button" :show="overflow === 'scrollX' && pingEnd" :on-scroll-right="onScrollRight">
      <el-button v-if="overflow === 'scrollX' && pingEnd" size="small" class="elx-attachments-next-btn" @click="onScrollRight">
        <el-icon><ArrowRightBold /></el-icon>
      </el-button>
    </slot>

    <!-- Drop area overlay (teleported) -->
    <Teleport v-if="targetElement && isTargetDrag" :to="targetElement">
      <slot name="drop-area">
        <div ref="dropAreaRef" class="elx-attachments-drop-area">
          <el-icon class="elx-attachments-drop-area-icon"><UploadFilled /></el-icon>
          <div class="elx-attachments-drop-area-text">在此处拖放文件上传</div>
        </div>
      </slot>
    </Teleport>
  </div>
</template>

<style scoped lang="scss">
@use './style.scss';
</style>
